import { useCallback, useEffect, useState } from 'react'
import { Bloc, Bouton, Icone, Puce, SquelettePage } from '../ui'
import { Bandeau, Champ, Liste, Zone } from './Champs'
import { ErreurApi } from '../api'
import {
  enregistrerDossier,
  lireDossier,
  type Dossier as DossierDto,
  type NatureRecherche,
  type Occupation,
  type TrancheRevenu,
  type TypeGarant,
} from '../apiRelations'
import { dateCourte, montant, nombre } from '../formats'
import { TEXTES, interpoler, messageErreur, type Langue } from '../i18n'
import { libelleVille, useVille } from '../VilleContexte'

/**
 * Dossier déclaratif — L4, revu le 13/09/2026 (maquette `docs/ui/pwa/mon-dossier.html`).
 *
 * **Aucune pièce, nulle part** (invariant 8). Pas de champ de téléversement, pas même désactivé :
 * un champ grisé est une promesse, et la promesse ici serait fausse. Le revenu se donne en
 * **tranche**, le garant par sa **nature** — jamais son identité.
 *
 * L'aparté « vu par un propriétaire » est l'invariant rendu visible plutôt qu'expliqué : le
 * demandeur voit ce qui sort — la tranche et non le montant, la nature du garant et non son nom —
 * et remplit mieux ce qui entre. Un demandeur qui ne comprend pas pourquoi on ne lui demande pas
 * sa CIN pensera que la plateforme est laxiste ; il faut qu'il sache que c'est un refus, pas un
 * oubli.
 */
const VIDE: DossierDto = {
  occupation: 'Student',
  institutionName: null,
  studyLevel: null,
  studyYear: null,
  employmentSector: null,
  contractType: null,
  seniorityMonths: null,
  incomeBand: 'Undisclosed',
  maxBudgetAllInclusive: 0,
  currencyCode: 'MAD',
  hasGuarantor: false,
  guarantorType: null,
  moveInWindowStart: aujourdhui(),
  moveInWindowEnd: dansTroisMois(),
  desiredDurationMonths: 12,
  searchKind: 'RoomInSharedHousing',
  cityCode: null,
  maxTravelMinutes: null,
  currentSituation: null,
  about: null,
  completedAt: null,
  amenityPreferences: [],
}

export function Dossier({ langue, nomAffiche }: { langue: Langue; nomAffiche: string }) {
  const t = TEXTES[langue]
  const { villes, courante, trouver } = useVille()

  const [dossier, setDossier] = useState<DossierDto | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState(false)
  const [envoi, setEnvoi] = useState(false)

  // « Ma ville » (L13) : un dossier sans ville prend celle où l'on cherche. Le serveur appliquerait
  // la ville par défaut, mais un demandeur de Casablanca matché à Salé ne comprendrait pas
  // pourquoi son fil est vide — et ne le dirait à personne.
  const charger = useCallback(() => {
    void lireDossier()
      .then((existant) => setDossier(existant ?? VIDE))
      .catch(() => setDossier(VIDE))
  }, [])

  useEffect(charger, [charger])

  const modifier = useCallback((champs: Partial<DossierDto>) => {
    setSucces(false)
    setDossier((precedent) => (precedent === null ? precedent : { ...precedent, ...champs }))
  }, [])

  const soumettre = useCallback(async () => {
    if (dossier === null || envoi) {
      return
    }

    setEnvoi(true)
    setErreur(null)

    try {
      await enregistrerDossier({ ...dossier, cityCode: dossier.cityCode ?? courante?.code ?? null })
      setSucces(true)
      charger()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnvoi(false)
    }
  }, [charger, dossier, envoi, langue, courante])

  if (dossier === null) {
    return <SquelettePage />
  }

  const etudiant = dossier.occupation === 'Student'
  const travaille = dossier.occupation === 'Employee' || dossier.occupation === 'SelfEmployed'
  const complet = dossier.completedAt !== null

  // La ville affichée : celle du dossier, sinon celle où l'on cherche. Elle part telle quelle à
  // l'enregistrement — un dossier ne reste jamais « sans ville » une fois passé par cet écran.
  const villeDossier = trouver(dossier.cityCode) ?? courante
  const villesProposees = villes.filter(
    (candidate) => candidate.status === 'Open' || candidate.code === villeDossier?.code,
  )

  const occupations = [
    { valeur: 'Student', libelle: t.occupationStudent },
    { valeur: 'Employee', libelle: t.occupationEmployee },
    { valeur: 'SelfEmployed', libelle: t.occupationSelfEmployed },
    { valeur: 'Other', libelle: t.occupationOther },
  ]
  const contrats = [
    { valeur: 'Cdi', libelle: 'CDI' },
    { valeur: 'Cdd', libelle: 'CDD' },
    { valeur: 'Interim', libelle: 'Intérim' },
    { valeur: 'Independant', libelle: t.occupationSelfEmployed },
    { valeur: 'Autre', libelle: t.occupationOther },
  ]
  const niveaux = [
    { valeur: 'Bac', libelle: 'Bac' },
    { valeur: 'BacPlusDeux', libelle: 'Bac+2' },
    { valeur: 'Licence', libelle: 'Licence' },
    { valeur: 'Master', libelle: 'Master' },
    { valeur: 'Doctorat', libelle: 'Doctorat' },
    { valeur: 'Autre', libelle: t.occupationOther },
  ]
  // Une TRANCHE. Le montant exact n'est ni demandé ni stocké — spec §6.3.
  const revenus = [
    { valeur: 'Undisclosed', libelle: t.revenuUndisclosed },
    { valeur: 'UnderThreeThousand', libelle: t.revenuUnderThreeThousand },
    { valeur: 'ThreeToFiveThousand', libelle: t.revenuThreeToFiveThousand },
    { valeur: 'FiveToEightThousand', libelle: t.revenuFiveToEightThousand },
    { valeur: 'EightToTwelveThousand', libelle: t.revenuEightToTwelveThousand },
    { valeur: 'OverTwelveThousand', libelle: t.revenuOverTwelveThousand },
  ]
  const garants = [
    { valeur: 'Family', libelle: t.garantFamily },
    { valeur: 'Employer', libelle: t.garantEmployer },
    { valeur: 'Institution', libelle: t.garantInstitution },
    { valeur: 'Other', libelle: t.garantOther },
  ]
  const natures = [
    { valeur: 'EntirePlace', libelle: t.natureEntirePlace },
    { valeur: 'RoomInSharedHousing', libelle: t.natureRoomInSharedHousing },
    { valeur: 'FormGroup', libelle: t.natureFormGroup },
  ]
  const libelle = (options: { valeur: string; libelle: string }[], valeur: string | null) =>
    options.find((option) => option.valeur === valeur)?.libelle ?? '—'

  // Ce que l'aparté résume : la situation en une ligne.
  const situation = [
    libelle(occupations, dossier.occupation),
    travaille && dossier.contractType !== null ? libelle(contrats, dossier.contractType) : null,
    travaille && dossier.seniorityMonths !== null
      ? interpoler(t.dossierAncienneteMois, { n: dossier.seniorityMonths }, langue)
      : null,
    etudiant ? dossier.institutionName : null,
    etudiant && dossier.studyLevel !== null ? libelle(niveaux, dossier.studyLevel) : null,
  ]
    .filter((partie): partie is string => partie !== null && partie !== '')
    .join(' · ')

  return (
    <section className="dossier">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.monDossier}</h2>
          <Puce ton={complet ? 'succes' : 'attention'}>
            {complet && <Icone nom="coche" taille="sm" />}
            {complet ? t.dossierComplet : t.dossierIncomplet}
          </Puce>
        </div>
        <p>{t.dossierIntro}</p>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {succes && <Bandeau ton="succes">{t.dossierEnregistre}</Bandeau>}

      <div className="avec-aparte">
        <form
          className="pile"
          onSubmit={(evenement) => {
            evenement.preventDefault()
            void soumettre()
          }}
        >
          <Bloc titre={t.dossierSituation}>
            <div className="formulaire formulaire--deux">
              <Liste
                id="occupation"
                libelle={t.occupation}
                valeur={dossier.occupation}
                options={occupations}
                onChange={(valeur) => modifier({ occupation: valeur as Occupation })}
              />

              {etudiant && (
                <>
                  <Champ
                    id="etablissement"
                    libelle={t.etablissement}
                    valeur={dossier.institutionName ?? ''}
                    onChange={(valeur) => modifier({ institutionName: valeur || null })}
                  />
                  <Liste
                    id="niveau"
                    libelle={t.niveauEtude}
                    valeur={dossier.studyLevel ?? 'Licence'}
                    options={niveaux}
                    onChange={(valeur) => modifier({ studyLevel: valeur as DossierDto['studyLevel'] })}
                  />
                  <Champ
                    id="annee"
                    type="number"
                    libelle={t.anneeEtude}
                    valeur={dossier.studyYear?.toString() ?? ''}
                    onChange={(valeur) => modifier({ studyYear: valeur === '' ? null : Number(valeur) })}
                  />
                </>
              )}

              {travaille && (
                <>
                  <Champ
                    id="secteur"
                    libelle={t.secteur}
                    valeur={dossier.employmentSector ?? ''}
                    onChange={(valeur) => modifier({ employmentSector: valeur || null })}
                  />
                  <Liste
                    id="contrat"
                    libelle={t.typeContrat}
                    valeur={dossier.contractType ?? 'Cdi'}
                    options={contrats}
                    onChange={(valeur) => modifier({ contractType: valeur as DossierDto['contractType'] })}
                  />
                  <Champ
                    id="anciennete"
                    type="number"
                    libelle={t.anciennete}
                    suffixe={t.uniteMois}
                    valeur={dossier.seniorityMonths?.toString() ?? ''}
                    onChange={(valeur) => modifier({ seniorityMonths: valeur === '' ? null : Number(valeur) })}
                  />
                </>
              )}
            </div>
          </Bloc>

          <Bloc titre={t.dossierCapacite}>
            <div className="formulaire formulaire--deux">
              <Liste
                id="revenu"
                libelle={t.trancheRevenu}
                valeur={dossier.incomeBand}
                options={revenus}
                aide={t.dossierRevenuAide}
                onChange={(valeur) => modifier({ incomeBand: valeur as TrancheRevenu })}
              />
              <Champ
                id="budget"
                type="number"
                libelle={t.budgetMax}
                suffixe={`${dossier.currencyCode} ${t.annonceParMois}`}
                valeur={dossier.maxBudgetAllInclusive.toString()}
                onChange={(valeur) => modifier({ maxBudgetAllInclusive: Number(valeur) })}
              />

              <div className="champ pleine-largeur">
                <label className="case" htmlFor="garant">
                  <input
                    id="garant"
                    type="checkbox"
                    checked={dossier.hasGuarantor}
                    onChange={(evenement) =>
                      modifier({
                        hasGuarantor: evenement.target.checked,
                        guarantorType: evenement.target.checked ? (dossier.guarantorType ?? 'Family') : null,
                      })
                    }
                  />
                  {t.aUnGarant}
                </label>
              </div>

              {dossier.hasGuarantor && (
                <Liste
                  id="type-garant"
                  libelle={t.typeGarant}
                  valeur={dossier.guarantorType ?? 'Family'}
                  options={garants}
                  aide={t.garantAide}
                  onChange={(valeur) => modifier({ guarantorType: valeur as TypeGarant })}
                />
              )}
            </div>
          </Bloc>

          <Bloc titre={t.natureRecherche}>
            <div className="formulaire formulaire--deux">
              {villesProposees.length > 0 && (
                <Liste
                  id="ville"
                  libelle={t.villeMaVille}
                  valeur={villeDossier?.code ?? ''}
                  options={villesProposees.map((candidate) => ({
                    valeur: candidate.code,
                    libelle:
                      candidate.status === 'Open'
                        ? libelleVille(candidate, langue)
                        : `${libelleVille(candidate, langue)} · ${t.villePasEncoreOuverte}`,
                  }))}
                  aide={t.villeDossierAide}
                  onChange={(valeur) => modifier({ cityCode: valeur })}
                />
              )}
              <Liste
                id="nature"
                libelle={t.dossierRechercheLibelle}
                valeur={dossier.searchKind}
                options={natures}
                onChange={(valeur) => modifier({ searchKind: valeur as NatureRecherche })}
              />
              <Champ
                id="duree"
                type="number"
                libelle={t.dureeMois}
                suffixe={t.uniteMois}
                valeur={dossier.desiredDurationMonths.toString()}
                onChange={(valeur) => modifier({ desiredDurationMonths: Number(valeur) })}
              />
              <Champ
                id="entree"
                type="date"
                libelle={t.entreeLe}
                valeur={dossier.moveInWindowStart}
                onChange={(valeur) => modifier({ moveInWindowStart: valeur })}
              />
              <Champ
                id="entree-fin"
                type="date"
                libelle={t.entreeJusqua}
                valeur={dossier.moveInWindowEnd}
                onChange={(valeur) => modifier({ moveInWindowEnd: valeur })}
              />
              <Champ
                id="trajet"
                type="number"
                libelle={t.dossierTrajetLibelle}
                suffixe={t.uniteMinutes}
                aide={t.dossierTrajetAide}
                valeur={dossier.maxTravelMinutes?.toString() ?? ''}
                onChange={(valeur) => modifier({ maxTravelMinutes: valeur === '' ? null : Number(valeur) })}
              />
              <Zone
                id="situation"
                libelle={t.situationActuelle}
                lignes={3}
                pleineLargeur
                valeur={dossier.currentSituation ?? ''}
                onChange={(valeur) => modifier({ currentSituation: valeur || null })}
              />
              <Zone
                id="apropos"
                libelle={t.aProposDeMoi}
                aide={t.dossierAProposAide}
                pleineLargeur
                valeur={dossier.about ?? ''}
                onChange={(valeur) => modifier({ about: valeur || null })}
              />
            </div>
          </Bloc>

          <div className="rangee rangee--fin">
            <Bouton apparence="discret" onClick={charger}>
              {t.annuler}
            </Bouton>
            <Bouton type="submit" charge={envoi}>
              {t.enregistrer}
            </Bouton>
          </div>
        </form>

        {/*
          Ce que verra un propriétaire — et rien de plus. La liste est courte à dessein : chaque
          ligne qu'on y ajouterait serait une donnée de plus qui sort.
        */}
        <aside className="aparte" aria-label={t.dossierVuPar}>
          <Bloc titre={t.dossierVuPar}>
            <div className="dossier__personne">
              <span className="avatar" aria-hidden="true">
                {initiales(nomAffiche)}
              </span>
              <div>
                <b>{nomAffiche}</b>
                <span className="secondaire">{situation}</span>
              </div>
            </div>
            <dl className="fiche__lignes" style={{ marginBlockStart: 'var(--e-4)' }}>
              <div className="fiche__ligne">
                <dt>{t.dossierRevenu}</dt>
                <dd>{libelle(revenus, dossier.incomeBand)}</dd>
              </div>
              <div className="fiche__ligne">
                <dt>{t.dossierBudget}</dt>
                <dd>
                  <bdi>{montant(dossier.maxBudgetAllInclusive, dossier.currencyCode, langue)}</bdi> {t.annonceParMois}
                </dd>
              </div>
              <div className="fiche__ligne">
                <dt>{t.dossierGarant}</dt>
                <dd>{dossier.hasGuarantor ? libelle(garants, dossier.guarantorType) : t.dossierSansGarant}</dd>
              </div>
              <div className="fiche__ligne">
                <dt>{t.dossierCherche}</dt>
                <dd>
                  {libelle(natures, dossier.searchKind)}
                  {villeDossier !== null && ` · ${libelleVille(villeDossier, langue)}`}
                </dd>
              </div>
              <div className="fiche__ligne">
                <dt>{t.dossierEntree}</dt>
                <dd>
                  {interpoler(t.dossierEntreeDuAu, {
                    debut: dateCourte(dossier.moveInWindowStart, langue),
                    fin: dateCourte(dossier.moveInWindowEnd, langue),
                  })}
                </dd>
              </div>
              <div className="fiche__ligne">
                <dt>{t.dossierDuree}</dt>
                <dd>
                  {nombre(dossier.desiredDurationMonths, langue)} {t.uniteMois}
                </dd>
              </div>
            </dl>
            {dossier.about !== null && dossier.about !== '' && (
              <p className="secondaire dossier__citation">« {dossier.about} »</p>
            )}
            <p className="tertiaire" style={{ marginBlockStart: 'var(--e-3)' }}>
              {t.dossierVuParAide}
            </p>
          </Bloc>
          <Bandeau ton="info" icone={<Icone nom="bouclier" />}>
            {t.dossierInvariant}
          </Bandeau>
        </aside>
      </div>
    </section>
  )
}

/** « Salma Bennani » → « SB ». Décoratif : le nom est écrit à côté. */
function initiales(nom: string): string {
  return nom
    .split(/\s+/)
    .filter((mot) => mot !== '')
    .slice(0, 2)
    .map((mot) => mot[0]?.toUpperCase() ?? '')
    .join('')
}

function aujourdhui(): string {
  return new Date().toISOString().slice(0, 10)
}

function dansTroisMois(): string {
  const date = new Date()
  date.setMonth(date.getMonth() + 3)

  return date.toISOString().slice(0, 10)
}
